import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, Calculator, CheckCircle2, Clock3, IndianRupee, MapPin, Package,
  Save, Settings2, ShieldCheck, Truck, WalletCards, AlertTriangle, RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

const blank = {
  currency: 'INR',
  delivery_base_fee: '',
  delivery_free_above: '',
  delivery_included_km: '',
  delivery_rate_per_km: '',
  delivery_max_fee: '',
  platform_fee: { type: 'fixed', value: '' },
  tax: { type: 'percentage', value: '' },
  pharmacy_commission: { type: 'percentage', value: '' },
  ranger_payout: { type: 'percentage', value: '' },
  minimum_order_value: '',
  maximum_order_value: '',
  max_quantity_per_item: '',
  pharmacy_search_radius_km: '',
  ranger_search_radius_km: '',
  offer_ttl_seconds: '',
};

function money(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function feeAmount(base, fee) {
  const value = money(fee?.value);
  return fee?.type === 'percentage' ? (base * value) / 100 : value;
}

function calculatePreview(form, subtotal, distance) {
  const sub = Math.max(0, money(subtotal));
  const d = Math.max(0, money(distance));
  const freeAbove = money(form.delivery_free_above);
  const included = money(form.delivery_included_km);
  const rate = money(form.delivery_rate_per_km);
  const base = money(form.delivery_base_fee);
  const maxFee = money(form.delivery_max_fee);

  let delivery = sub >= freeAbove && freeAbove > 0 ? 0 : base;
  if (delivery > 0) delivery += Math.max(0, d - included) * rate;
  if (maxFee > 0) delivery = Math.min(delivery, maxFee);

  const platform = feeAmount(sub, form.platform_fee);
  const taxable = sub + delivery + platform;
  const tax = feeAmount(taxable, form.tax);
  const total = taxable + tax;
  const commission = feeAmount(sub, form.pharmacy_commission);
  const payout = feeAmount(delivery, form.ranger_payout);

  return {
    delivery: Math.round(delivery * 100) / 100,
    platform: Math.round(platform * 100) / 100,
    tax: Math.round(tax * 100) / 100,
    total: Math.round(total * 100) / 100,
    commission: Math.round(commission * 100) / 100,
    payout: Math.round(payout * 100) / 100,
  };
}

function Field({ label, value, onChange, help, suffix, min = '0', step = '0.01' }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-semibold text-slate-600">{label}</Label>
      <div className="relative">
        <Input type="number" min={min} step={step} value={value ?? ''} onChange={(e) => onChange(e.target.value)} className={suffix ? 'pr-12' : ''} />
        {suffix && <span className="absolute right-3 top-2.5 text-xs font-semibold text-slate-400">{suffix}</span>}
      </div>
      {help && <p className="text-[11px] leading-4 text-slate-500">{help}</p>}
    </div>
  );
}

function FeeField({ title, fee, onChange, help }) {
  return (
    <div className="space-y-2">
      <div>
        <Label className="text-xs font-semibold text-slate-600">{title}</Label>
        {help && <p className="text-[11px] text-slate-500 mt-1">{help}</p>}
      </div>
      <div className="grid grid-cols-[1fr_1fr] gap-2">
        <Select value={fee?.type || 'fixed'} onValueChange={(type) => onChange({ ...fee, type })}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="fixed">Fixed ₹</SelectItem>
            <SelectItem value="percentage">Percentage %</SelectItem>
          </SelectContent>
        </Select>
        <Input
          type="number"
          min="0"
          max={fee?.type === 'percentage' ? '100' : undefined}
          step="0.01"
          value={fee?.value ?? ''}
          onChange={(e) => onChange({ ...fee, value: e.target.value })}
          placeholder="Value"
        />
      </div>
    </div>
  );
}

export default function AdminPharmacyPricing() {
  const [form, setForm] = useState(blank);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [complete, setComplete] = useState(false);
  const [reasons, setReasons] = useState([]);
  const [version, setVersion] = useState(0);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [pharmacyLive, setPharmacyLive] = useState(false);
  const [liveUpdating, setLiveUpdating] = useState(false);
  const [sampleSubtotal, setSampleSubtotal] = useState('500');
  const [sampleDistance, setSampleDistance] = useState('5');

  const load = async () => {
    setLoading(true);
    try {
      const [{ data }, settings] = await Promise.all([
        api.get('/admin/pharmacy/pricing'),
        api.get('/admin/pharmacy/settings'),
      ]);
      setPharmacyLive(Boolean(settings.data?.enabled));
      setForm({ ...blank, ...(data.pricing || {}) });
      setComplete(Boolean(data.pricing_complete));
      setReasons(data.pricing_incomplete_reasons || []);
      setVersion(Number(data.pricing?.pricing_version || 0));
      setUpdatedAt(data.pricing?.updated_at || null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load Pharmacy pricing');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const update = (key, value) => setForm((p) => ({ ...p, [key]: value }));
  const updateFee = (key, value) => setForm((p) => ({ ...p, [key]: value }));

  const preview = useMemo(
    () => calculatePreview(form, sampleSubtotal, sampleDistance),
    [form, sampleSubtotal, sampleDistance],
  );

  const togglePharmacyLive = async () => {
    setLiveUpdating(true);
    try {
      const { data } = await api.patch('/admin/pharmacy/settings', { enabled: !pharmacyLive });
      setPharmacyLive(Boolean(data.enabled));
      toast.success(data.enabled ? 'Pharmacy is now live for new orders' : 'Pharmacy is offline for new orders');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not update Pharmacy live status');
    } finally {
      setLiveUpdating(false);
    }
  };

  const validate = () => {
    const errors = [];
    const percentageFields = [
      ['platform_fee', 'Platform fee'],
      ['tax', 'Tax'],
      ['pharmacy_commission', 'Pharmacy commission'],
      ['ranger_payout', 'Ranger payout'],
    ];
    for (const [key, label] of percentageFields) {
      if (!['fixed', 'percentage'].includes(form[key]?.type)) errors.push(label + ' type is invalid');
      const v = money(form[key]?.value);
      if (v < 0) errors.push(label + ' value cannot be negative');
      if (form[key]?.type === 'percentage' && v > 100) errors.push(label + ' percentage cannot exceed 100');
    }
    if (money(form.delivery_max_fee) < money(form.delivery_base_fee)) errors.push('Maximum delivery fee cannot be below the base fee');
    if (money(form.minimum_order_value) > money(form.maximum_order_value)) errors.push('Minimum order value cannot exceed maximum order value');
    if (money(form.maximum_order_value) <= 0) errors.push('Maximum order value must be greater than 0');
    return errors;
  };

  const save = async () => {
    const errors = validate();
    if (errors.length) {
      setComplete(false);
      setReasons(errors);
      toast.error(errors[0]);
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        delivery_base_fee: Number(form.delivery_base_fee),
        delivery_free_above: Number(form.delivery_free_above),
        delivery_included_km: Number(form.delivery_included_km),
        delivery_rate_per_km: Number(form.delivery_rate_per_km),
        delivery_max_fee: Number(form.delivery_max_fee),
        platform_fee: { type: form.platform_fee.type, value: Number(form.platform_fee.value) },
        tax: { type: form.tax.type, value: Number(form.tax.value) },
        pharmacy_commission: { type: form.pharmacy_commission.type, value: Number(form.pharmacy_commission.value) },
        ranger_payout: { type: form.ranger_payout.type, value: Number(form.ranger_payout.value) },
        minimum_order_value: Number(form.minimum_order_value),
        maximum_order_value: Number(form.maximum_order_value),
        max_quantity_per_item: Number(form.max_quantity_per_item),
        pharmacy_search_radius_km: Number(form.pharmacy_search_radius_km),
        ranger_search_radius_km: Number(form.ranger_search_radius_km),
        offer_ttl_seconds: Number(form.offer_ttl_seconds),
      };
      const { data } = await api.patch('/admin/pharmacy/pricing', payload);
      setForm({ ...blank, ...(data.pricing || {}) });
      setComplete(Boolean(data.pricing_complete));
      setReasons(data.pricing_incomplete_reasons || []);
      setVersion(Number(data.pricing?.pricing_version || 0));
      setUpdatedAt(data.pricing?.updated_at || null);
      toast.success(data.pricing_complete ? 'Pharmacy pricing saved and ready' : 'Pricing saved — review the remaining warnings');
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (Array.isArray(detail)) setReasons(detail.map((x) => x.msg || x));
      toast.error(typeof detail === 'string' ? detail : 'Could not save Pharmacy pricing');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="admin-page"><div className="text-sm text-slate-500">Loading Pharmacy controls…</div></div>;
  }

  return (
    <div className="admin-page" data-testid="admin-pharmacy-pricing">
      <div className="admin-page-head">
        <div>
          <div className="admin-eyebrow"><Settings2 size={13} /> Pharmacy commercial controls</div>
          <h1>Pricing & Dispatch</h1>
          <p>One control centre for what the customer pays, what the pharmacy earns, what the Ranger gets, and how far RESQLY searches.</p>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          <button
            className={pharmacyLive && complete ? 'admin-lux-button' : 'admin-lux-button secondary'}
            onClick={togglePharmacyLive}
            disabled={liveUpdating || (!pharmacyLive && !complete)}
            title={pharmacyLive ? 'Turn Pharmacy off for new intake' : 'Save valid pricing before turning Pharmacy on'}
          >
            {pharmacyLive && complete ? <CheckCircle2 size={16} /> : <AlertTriangle size={16} />}
            {liveUpdating ? 'Updating…' : pharmacyLive ? (complete ? 'Pharmacy Live' : 'Live · Pricing Required') : 'Pharmacy Offline'}
          </button>
          <button className="admin-lux-button secondary" onClick={load}><RefreshCw size={16} /> Refresh</button>
          <Link to="/admin/pharmacy" className="admin-lux-button secondary"><ArrowLeft size={16} /> Pharmacy</Link>
          <button className="admin-lux-button" onClick={save} disabled={saving}><Save size={16} /> {saving ? 'Saving…' : 'Save changes'}</button>
        </div>
      </div>

      <div className="admin-dashboard-grid mb-5">
        <div className="admin-stat-card">
          <span>Pricing status</span>
          <strong className={complete ? 'text-emerald-700' : 'text-amber-700'}>{complete ? 'READY' : 'ACTION'}</strong>
          <small>{complete ? 'Can be used operationally' : 'Complete the controls below'}</small>
        </div>
        <div className="admin-stat-card"><span>Version</span><strong>v{version}</strong><small>{updatedAt ? new Date(updatedAt).toLocaleString() : 'Not saved yet'}</small></div>
        <div className="admin-stat-card"><span>Default currency</span><strong><IndianRupee size={17} className="inline" /> INR</strong><small>Rupee pricing only</small></div>
        <div className="admin-stat-card"><span>Dispatch model</span><strong>Radius</strong><small>Nearest verified operators first</small></div>
      </div>

      {pharmacyLive && !complete && (
        <div className="admin-card mb-5 border-red-200 bg-red-50/50">
          <div className="flex items-start gap-3">
            <AlertTriangle className="text-red-600 mt-0.5" size={19} />
            <div>
              <h2 className="text-sm font-semibold text-red-900">Pharmacy is marked Live, but intake is blocked</h2>
              <p className="text-xs text-red-800 mt-1 leading-5">Admin pricing has not been saved as a valid version yet. Customers cannot start new Pharmacy orders until you save pricing.</p>
            </div>
          </div>
        </div>
      )}

      {!complete && (
        <div className="admin-card mb-5 border-amber-200">
          <div className="flex items-start gap-3">
            <AlertTriangle className="text-amber-600 mt-0.5" size={19} />
            <div>
              <h2 className="text-sm font-semibold text-slate-900">Pharmacy controls are not ready</h2>
              <p className="text-xs text-slate-500 mt-1">These are configuration checks, not generic service-pricing requirements. Fix the items below, save, then activate the service.</p>
              <ul className="mt-3 space-y-1 text-xs text-amber-800">
                {reasons.map((reason) => <li key={reason}>• {reason}</li>)}
              </ul>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-[1.65fr_1fr] gap-5">
        <div className="space-y-5">
          <section className="admin-card">
            <div className="admin-card-head">
              <div><h2>Delivery pricing</h2><p>Customer-facing delivery charge. Distance is calculated from customer to the matched pharmacy.</p></div>
              <Truck size={20} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Base delivery fee" value={form.delivery_base_fee} onChange={(v) => update('delivery_base_fee', v)} help="Starting charge before distance." suffix="₹" />
              <Field label="Free delivery above" value={form.delivery_free_above} onChange={(v) => update('delivery_free_above', v)} help="Orders at or above this subtotal get free delivery." suffix="₹" />
              <Field label="Included distance" value={form.delivery_included_km} onChange={(v) => update('delivery_included_km', v)} help="Distance included inside the base delivery fee." suffix="km" />
              <Field label="Extra distance rate" value={form.delivery_rate_per_km} onChange={(v) => update('delivery_rate_per_km', v)} help="Added for each km after included distance." suffix="₹/km" />
              <Field label="Maximum delivery fee" value={form.delivery_max_fee} onChange={(v) => update('delivery_max_fee', v)} help="Hard customer-facing cap." suffix="₹" />
              <div className="rounded-xl bg-slate-50 p-3 text-xs text-slate-600 flex items-start gap-2">
                <MapPin size={15} className="mt-0.5 text-slate-400" />
                <span>Catalog orders show the base estimate before pharmacy selection. Prescription orders are recalculated using the actual matched-pharmacy distance.</span>
              </div>
            </div>
          </section>

          <section className="admin-card">
            <div className="admin-card-head"><div><h2>Platform charges</h2><p>Charges added to the customer's checkout total.</p></div><IndianRupee size={20} /></div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <FeeField title="Platform / convenience fee" fee={form.platform_fee} onChange={(v) => updateFee('platform_fee', v)} help="Fixed fee or percentage of medicine subtotal." />
              <FeeField title="Tax / GST" fee={form.tax} onChange={(v) => updateFee('tax', v)} help="Calculated on subtotal + delivery + platform fee." />
            </div>
          </section>

          <section className="admin-card">
            <div className="admin-card-head"><div><h2>Settlement & payouts</h2><p>Internal economics. These values do not get added again to the customer total.</p></div><WalletCards size={20} /></div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <FeeField title="Pharmacy commission" fee={form.pharmacy_commission} onChange={(v) => updateFee('pharmacy_commission', v)} help="RESQLY's commission calculated on medicine subtotal." />
              <FeeField title="Ranger payout" fee={form.ranger_payout} onChange={(v) => updateFee('ranger_payout', v)} help="Ranger earning calculated from delivery fee." />
            </div>
          </section>

          <section className="admin-card">
            <div className="admin-card-head"><div><h2>Order controls</h2><p>Guardrails that apply before an order is accepted for payment.</p></div><Package size={20} /></div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Field label="Minimum order value" value={form.minimum_order_value} onChange={(v) => update('minimum_order_value', v)} suffix="₹" />
              <Field label="Maximum order value" value={form.maximum_order_value} onChange={(v) => update('maximum_order_value', v)} suffix="₹" />
              <Field label="Max quantity / item" value={form.max_quantity_per_item} onChange={(v) => update('max_quantity_per_item', v)} step="1" />
            </div>
          </section>

          <section className="admin-card">
            <div className="admin-card-head"><div><h2>Dispatch controls</h2><p>Operator search radius and how long each offer remains claimable.</p></div><ShieldCheck size={20} /></div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Field label="Pharmacy search radius" value={form.pharmacy_search_radius_km} onChange={(v) => update('pharmacy_search_radius_km', v)} suffix="km" />
              <Field label="Ranger search radius" value={form.ranger_search_radius_km} onChange={(v) => update('ranger_search_radius_km', v)} suffix="km" />
              <Field label="Offer TTL" value={form.offer_ttl_seconds} onChange={(v) => update('offer_ttl_seconds', v)} suffix="sec" step="1" />
            </div>
            <div className="mt-4 rounded-xl border border-slate-200 p-3 text-xs text-slate-600">
              <strong className="text-slate-800">Fallback behaviour:</strong> expired/declined pharmacy offers move to another match attempt; expired/declined Ranger offers recover to Ranger search. No order is allowed to hang silently.
            </div>
          </section>
        </div>

        <div className="space-y-5">
          <section className="admin-card sticky top-5">
            <div className="admin-card-head">
              <div><h2>Live quote preview</h2><p>Use this to sanity-check your economics before saving.</p></div>
              <Calculator size={20} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Medicine subtotal" value={sampleSubtotal} onChange={setSampleSubtotal} suffix="₹" />
              <Field label="Pharmacy distance" value={sampleDistance} onChange={setSampleDistance} suffix="km" />
            </div>
            <div className="mt-4 divide-y border rounded-xl overflow-hidden">
              <div className="flex justify-between px-3 py-2.5 text-sm"><span>Medicine subtotal</span><span>₹{money(sampleSubtotal).toFixed(2)}</span></div>
              <div className="flex justify-between px-3 py-2.5 text-sm"><span>Delivery</span><span>₹{preview.delivery.toFixed(2)}</span></div>
              <div className="flex justify-between px-3 py-2.5 text-sm"><span>Platform fee</span><span>₹{preview.platform.toFixed(2)}</span></div>
              <div className="flex justify-between px-3 py-2.5 text-sm"><span>Tax</span><span>₹{preview.tax.toFixed(2)}</span></div>
              <div className="flex justify-between px-3 py-3 bg-slate-50 font-bold"><span>Customer pays</span><span>₹{preview.total.toFixed(2)}</span></div>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3">
              <div className="rounded-xl bg-emerald-50 p-3"><div className="text-[11px] text-emerald-700">Pharmacy commission</div><strong className="text-emerald-900">₹{preview.commission.toFixed(2)}</strong></div>
              <div className="rounded-xl bg-blue-50 p-3"><div className="text-[11px] text-blue-700">Ranger payout</div><strong className="text-blue-900">₹{preview.payout.toFixed(2)}</strong></div>
            </div>
            <div className="mt-4 text-xs text-slate-500 leading-5">
              Commission is an internal settlement deduction. Ranger payout is an internal delivery earning. Neither is charged twice to the customer.
            </div>
          </section>

          <section className="admin-card">
            <div className="flex items-start gap-3">
              {complete ? <CheckCircle2 className="text-emerald-600 mt-0.5" size={20} /> : <Clock3 className="text-amber-600 mt-0.5" size={20} />}
              <div>
                <h2>Before you activate Pharmacy</h2>
                <p className="text-xs text-slate-500 mt-1 leading-5">Save once, verify the preview, then turn on the Pharmacy operational switch from Services. Existing orders keep their own frozen pricing snapshot.</p>
              </div>
            </div>
            <Button className="w-full mt-4 bg-slate-900 hover:bg-slate-800" onClick={save} disabled={saving}>
              <Save size={16} className="mr-2" /> {saving ? 'Saving…' : 'Save Pharmacy Controls'}
            </Button>
          </section>
        </div>
      </div>
    </div>
  );
}
